import { effect, signal } from "@preact-signals/safe-react";

import type { OrgNodeChangedEventDetail } from "@pdpp/lib-planta";
import { ORG_NODE_CHANGED_EVENT, ORG_NODE_STORAGE_KEY } from "@pdpp/lib-planta";

import { View } from "../../types/client";
import {
  getShareableSetting,
  setShareableSetting,
} from "../../utils/shareableSetting";
import {
  buildOrgNodeKey,
  clearAncestorsCache,
  getAncestors,
  resolveExternalOrgNode,
} from "./transforms";
import type { ClientBusinessNode } from "./types";

// Single composite key "orgNode" with value
// "TYPE:PUBLIC_ID[:division:DIV[:department:DEPT]]" (e.g. "STO:556:division:1234").
// This replaces the previous approach of separate orgNode, orgPublicNode, and orgPublicNodeType keys.
const KEY = ORG_NODE_STORAGE_KEY;

const storageView = localStorage.getItem("view") as View;

export const orgId$ = signal(getShareableSetting(KEY) ?? undefined);
export const orgNodes$ = signal<ReadonlyArray<ClientBusinessNode>>([]);
export const selectedUnit$ = signal<ClientBusinessNode | undefined>(undefined);
export const countryCode$ = signal<string | undefined>("SE");

export const selectedForecastView$ = signal<View | undefined>(
  storageView ?? View.Period,
);
export const searchQuery$ = signal<string>("");
export const activitySearchQuery$ = signal<string>("");
type OrgNodeById = Map<string, ClientBusinessNode>;

export const ORG_NODE_BY_ID: OrgNodeById = new Map();

let isUpdatingFromListener = false;
let isDispatchingEvent = false;
let isInitialized = false;

let pendingExternalOrgNode: string | undefined;

export const setUpdatingFromListener = (value: boolean): void => {
  isUpdatingFromListener = value;
};

let lastDispatchedOrgNode = "";

effect(() => {
  const value = orgId$.value;
  const orgNodes = orgNodes$.value;

  if (!isInitialized) {
    isInitialized = true;
    return;
  }

  if (isUpdatingFromListener) {
    isUpdatingFromListener = false;
    return;
  }

  if (value === undefined || value === "") return;
  if (orgNodes.length === 0) return;

  try {
    const ancestors = getAncestors(orgNodes, value);
    const hasUnitAncestor = ancestors.some(
      (ancestor) => ancestor.type === "unit",
    );

    if (!hasUnitAncestor) {
      const resolvedUnit = resolveExternalOrgNode(value, orgNodes);
      if (resolvedUnit === null) {
        console.warn("Could not resolve org node for orgId:", value);
      } else {
        orgId$.value = resolvedUnit;
      }
      return;
    }

    const compositeKey = buildOrgNodeKey(orgNodes, value);
    if (compositeKey === undefined) return;
    if (compositeKey === lastDispatchedOrgNode) return;
    lastDispatchedOrgNode = compositeKey;

    setShareableSetting(KEY, compositeKey);

    if (!isDispatchingEvent) {
      isDispatchingEvent = true;
      window.dispatchEvent(
        new CustomEvent(ORG_NODE_CHANGED_EVENT, {
          detail: { orgNode: compositeKey },
        }),
      );
      isDispatchingEvent = false;
    }
  } catch (error) {
    console.error("Error in org node dispatch effect:", error);
    isDispatchingEvent = false;
    isUpdatingFromListener = false;
  }
});

// Populates orgNodes Map
effect(() => {
  const orgNodes = orgNodes$.value;

  ORG_NODE_BY_ID.clear();
  clearAncestorsCache();
  for (const node of orgNodes) {
    ORG_NODE_BY_ID.set(node.id, node);
  }
});

effect(() => {
  const orgNodes = orgNodes$.value;
  const orgIg = orgId$.value;

  const ancestors = getAncestors(orgNodes, orgIg);
  const selectedUnit = ancestors.find((ancestor) => ancestor.type === "unit");

  if (selectedUnit) {
    selectedUnit$.value = selectedUnit;
    countryCode$.value = selectedUnit.parentId;
  }
});

effect(() => {
  if (selectedForecastView$.value !== undefined) {
    localStorage.setItem("view", selectedForecastView$.value ?? "");
  }
});

if (typeof window !== "undefined") {
  /**
   * Resolve an external composite key (e.g. "STO:556") to the internal org node id.
   * Always requires both type and publicId for a safe lookup.
   */

  const handleOrgNodeChange = (compositeKey: string): void => {
    if (isDispatchingEvent || !compositeKey || compositeKey === "") return;

    const internalId = resolveExternalOrgNode(compositeKey, orgNodes$.value);

    // Org data may not have loaded yet; keep the value and apply it once it has,
    // instead of recording it as dispatched and later overwriting it.
    if (internalId === null || internalId === "") {
      pendingExternalOrgNode = compositeKey;
      return;
    }

    pendingExternalOrgNode = undefined;

    // Record the key WLP would publish for the resolved node, not the inbound
    // string: if we could only resolve part of the path, republishing the
    // shallower key would truncate the shared value for the other apps.
    lastDispatchedOrgNode =
      buildOrgNodeKey(orgNodes$.value, internalId) ?? compositeKey;

    if (internalId !== orgId$.value) {
      isUpdatingFromListener = true;
      orgId$.value = internalId;
    }
  };

  effect(() => {
    orgNodes$.value;
    if (pendingExternalOrgNode === undefined) return;
    handleOrgNodeChange(pendingExternalOrgNode);
  });

  window.addEventListener(ORG_NODE_CHANGED_EVENT, ((
    event: CustomEvent<OrgNodeChangedEventDetail>,
  ) => {
    const orgNode = event.detail?.orgNode;
    if (orgNode) {
      handleOrgNodeChange(orgNode);
    }
  }) as EventListener);

  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key != null && event.key.startsWith("msal.")) {
      return;
    }

    if (event.key === KEY && event.newValue !== null && event.newValue !== "") {
      try {
        // The orgNode localStorage value may be an internal id or a composite key.
        // Try composite key resolution first; fall back to direct internal id.
        const nodes = orgNodes$.value;
        const directMatch = nodes.find((n) => n.id === event.newValue);
        if (directMatch) {
          if (event.newValue !== orgId$.value) {
            isUpdatingFromListener = true;
            orgId$.value = event.newValue;
          }
        } else {
          handleOrgNodeChange(event.newValue);
        }
      } finally {
        isUpdatingFromListener = false;
      }
    }
  });
}
